/**
 * AccountrixLogo (iter 72m) — brand mark for Accountrix.
 * A stylised leaf-and-ledger emblem alongside the "accountrix" wordmark.
 * Renders in three variants:
 *   • mark      : just the leaf badge (great for favicons, avatar cells)
 *   • wordmark  : just the type (great for top-bar with an icon nearby)
 *   • full      : badge + wordmark side-by-side (default; login page, PDFs, marketing)
 *
 * Colours pull from the Botanical Ledger palette so the same component
 * reads well on light and dark surfaces. `tone="dark"` (default) uses
 * the deep-forest wordmark; `tone="light"` inverts to white for hero
 * backgrounds.
 */

const LEAF_MID = "#3A8760";
const LEAF_DEEP = "#275E43";
const LEAF_DARK = "#132A1F";

const SIZES = {
  sm: { badge: 28, gap: "gap-2", type: "text-lg", tag: "text-[9px]" },
  md: { badge: 36, gap: "gap-2.5", type: "text-xl", tag: "text-[10px]" },
  lg: { badge: 44, gap: "gap-3", type: "text-2xl", tag: "text-[11px]" },
  xl: { badge: 56, gap: "gap-3", type: "text-3xl", tag: "text-xs" },
};

function LeafBadge({ size = 36, className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 44 44"
      className={className}
      role="img"
      aria-label="Accountrix mark"
    >
      <defs>
        <linearGradient id="accx-badge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={LEAF_MID} />
          <stop offset="100%" stopColor={LEAF_DEEP} />
        </linearGradient>
        <linearGradient id="accx-leaf" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#EAF7EF" />
          <stop offset="100%" stopColor="#B7DEC5" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="42" height="42" rx="11" fill="url(#accx-badge)" />
      <rect x="1" y="1" width="42" height="42" rx="11" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
      {/* Stylised leaf: elongated teardrop with a central vein. Symbolises
          "personal finance" (organic growth) and "business ledger" (the
          vein reads as a rule line). */}
      <path
        d="M22 8 C 30 12, 34 20, 30 32 C 24 34, 16 32, 14 24 C 12 16, 16 10, 22 8 Z"
        fill="url(#accx-leaf)"
        opacity="0.95"
      />
      <path
        d="M22 9 L 22 32"
        stroke={LEAF_DEEP}
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.55"
      />
      {/* small ledger tick marks that echo double-entry rows */}
      <path
        d="M17 18 L 21.4 18 M17 22 L 21.4 22 M17 26 L 21.4 26 M22.6 18 L 27 18 M22.6 22 L 27 22 M22.6 26 L 27 26"
        stroke={LEAF_DEEP}
        strokeWidth="0.9"
        strokeLinecap="round"
        opacity="0.35"
      />
    </svg>
  );
}

export function AccountrixLogo({
  variant = "full",
  size = "md",
  tone = "dark",
  className = "",
  testId,
}) {
  const s = SIZES[size] || SIZES.md;
  const wordColour = tone === "light" ? "text-white" : "text-[#0F2018]";
  const tagColour = tone === "light" ? "text-[#7ED4A6]" : "text-[#2F6B4A]";

  if (variant === "mark") {
    return <LeafBadge size={s.badge} className={className} />;
  }
  const Wordmark = (
    <div className="flex flex-col leading-none">
      <span
        className={`font-display font-semibold tracking-tight ${s.type} ${wordColour}`}
        style={{ letterSpacing: "-0.015em" }}
      >
        accountrix
      </span>
      <span
        className={`font-medium tracking-[0.22em] uppercase mt-1 ${s.tag} ${tagColour}`}
      >
        botanical ledger
      </span>
    </div>
  );
  if (variant === "wordmark") {
    return <div className={className} data-testid={testId}>{Wordmark}</div>;
  }
  return (
    <div className={`inline-flex items-center ${s.gap} ${className}`} data-testid={testId}>
      <LeafBadge size={s.badge} />
      {Wordmark}
    </div>
  );
}

export default AccountrixLogo;
